import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { providerLocationOperationsService } from '../../services/ProviderLocationOperationsService';
import type {
  SiteTagFleetSummaryRow,
  SiteTagFleetTagRow,
} from '@cnapse/shared/schemas.js';
import TagFleetSplitView from '../../components/location/TagFleetSplitView';

export default function SiteTagFleetDashboard() {
  const [sites, setSites] = useState<SiteTagFleetSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let cursor: string | undefined;
      const allSites: SiteTagFleetSummaryRow[] = [];

      do {
        const result = await providerLocationOperationsService.getSiteTagFleetList({ cursor, limit: 200 });
        allSites.push(...result.data.sites);
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);

      const tenantsData = await providerTenantsService.getTenants();
      setSites(allSites);
      setTenants(tenantsData as Tenant[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Node fleet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>(tenants.map((t) => [String(t.id), t.name]));
    return (id: string) => map.get(id) ?? null;
  }, [tenants]);

  const filteredSites = useMemo(() => {
    if (!tenantId) return sites;
    return sites.filter((s) => s.tenantId === tenantId);
  }, [sites, tenantId]);

  const loadTags = useCallback(
    async (site: SiteTagFleetSummaryRow): Promise<SiteTagFleetTagRow[]> => {
      let cursor: string | undefined;
      const allTags: SiteTagFleetTagRow[] = [];
      do {
        const result = await providerLocationOperationsService.getSiteTagFleetTags(site.tenantId, site.siteId, {
          cursor,
          limit: 200,
        });
        allTags.push(...result.data.tags);
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);
      return allTags;
    },
    [],
  );

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <AppListPage>
      
      {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

      <FormControl size="small" sx={{ mb: 2, minWidth: 220, flexShrink: 0 }}>
        <InputLabel id="tag-fleet-tenant-filter" shrink>
          Tenant
        </InputLabel>
        <Select
          labelId="tag-fleet-tenant-filter"
          label="Tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          displayEmpty
          notched
          renderValue={(value) => {
            if (!value) {
              return <Box component="span" sx={{ color: 'text.secondary' }}>All tenants</Box>;
            }
            return tenantNameById(value) ?? value;
          }}
        >
          <MenuItem value="">All tenants</MenuItem>
          {tenants.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TagFleetSplitView
          sites={filteredSites}
          sitesLoading={loading}
          showTenantOnCards={!tenantId}
          tenantNameById={tenantNameById}
          loadTags={loadTags}
          showDeviceMetadata
        />
      </Box>
    </AppListPage>
  );
}
